import { Link } from 'components/Link';
import { List, ListItem } from 'components/List';
import {
  ProjectSection,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { tiers } from '../data';
import styles from '../Stack.module.css';

/* What I build in and what I run the rest of it on. Notes only where I have
   an actual opinion -- "Terraform for infrastructure as code" tells you
   nothing you could not have guessed from the word Terraform. */

function Section({ heading, children }) {
  return (
    <ProjectSection padding="none" className={styles.section}>
      <ProjectSectionContent>
        <ProjectTextRow stretch width="m">
          <ProjectSectionHeading>{heading}</ProjectSectionHeading>
          <ProjectSectionText as="div">
            <List>{children}</List>
          </ProjectSectionText>
        </ProjectTextRow>
      </ProjectSectionContent>
    </ProjectSection>
  );
}

export function SoftwareContent() {
  return (
    <>
      <Section heading="Editors">
        <ListItem>
          <Link href="https://www.jetbrains.com/">JetBrains</Link> (PyCharm,
          GoLand, WebStorm, IntelliJ). Nothing else has the debugger, the
          database tooling and the VCS integration all in one window.
        </ListItem>
        <ListItem>
          <Link href="https://zed.dev">Zed</Link> with JetBrains keybindings,
          for anything too small to open an IDE for.
        </ListItem>
        <ListItem>Catppuccin Mocha across the terminal, Dracula in Zed.</ListItem>
      </Section>

      <Section heading="Terminal">
        <ListItem>
          <Link href="https://ghostty.org">Ghostty</Link>
        </ListItem>
        <ListItem>
          <Link href="https://fishshell.com">fish</Link> with a{' '}
          <Link href="https://starship.rs">Starship</Link> prompt
        </ListItem>
        <ListItem>
          <Link href="https://github.com/tmux/tmux">tmux</Link> for multiplexing,
          with <Link href="https://github.com/raine/workmux">workmux</Link> on
          top to run agent sessions in their own git worktrees
        </ListItem>
        <ListItem>
          <Link href="https://claude.com/claude-code">Claude Code</Link> and{' '}
          <Link href="https://github.com/openai/codex">Codex</Link> as the
          coding agents in those sessions
        </ListItem>
        <ListItem>
          <Link href="https://yazi-rs.github.io">Yazi</Link> for browsing files,
          with <Link href="https://github.com/charmbracelet/glow">Glow</Link>{' '}
          rendering the markdown previews
        </ListItem>
        <ListItem>
          <Link href="https://github.com/BurntSushi/ripgrep">ripgrep</Link>,{' '}
          <Link href="https://github.com/sharkdp/fd">fd</Link>,{' '}
          <Link href="https://github.com/junegunn/fzf">fzf</Link>,{' '}
          <Link href="https://github.com/ajeetdsouza/zoxide">zoxide</Link> and{' '}
          <Link href="https://github.com/eza-community/eza">eza</Link> in place of the
          stock Unix tools
        </ListItem>
        <ListItem>
          <Link href="https://cli.github.com">gh</Link> for GitHub from the shell
        </ListItem>
        <ListItem>
          <Link href="https://just.systems">just</Link> for task running
        </ListItem>
      </Section>

      <Section heading="Building">
        <ListItem>Python, always with <Link href="https://docs.astral.sh/uv/">uv</Link> and never pip</ListItem>
        <ListItem>Go</ListItem>
        <ListItem>TypeScript, React, Next.js</ListItem>
        <ListItem>
          Docker on <Link href="https://orbstack.dev">OrbStack</Link>,
          Kubernetes, Terraform, GitHub Actions
        </ListItem>
      </Section>

      <Section heading="Writing it down">
        <ListItem>
          <Link href="https://astro.build">Astro</Link> with{' '}
          <Link href="https://starlight.astro.build">Starlight</Link>. Docs that
          live next to the code are the only ones that stay true.
        </ListItem>
        <ListItem>
          <Link href="https://mermaid.js.org">Mermaid</Link> for every diagram,
          because it survives code review in a way an image never does.
        </ListItem>
        <ListItem>
          <Link href="https://excalidraw.com">Excalidraw</Link> when it needs to
          look hand-drawn. Still loses to a real whiteboard.
        </ListItem>
      </Section>

      <Section heading="Running the day">
        <ListItem>
          <Link href="https://raycast.com">Raycast</Link> as launcher, window
          manager and clipboard history. It ate about four other apps.
        </ListItem>
        <ListItem>
          <Link href="https://linear.app">Linear</Link> for tickets
        </ListItem>
        <ListItem>
          <Link href="https://notion.so">Notion</Link>, kept deliberately
          simple. Notes, reading list, project tracking. The trick is building
          something low-friction enough that you actually keep using it.
        </ListItem>
        <ListItem>
          <Link href="https://www.figma.com">Figma</Link>, and the Adobe suite
          when Figma won&apos;t do it
        </ListItem>
      </Section>

      <Section heading="Elsewhere">
        <ListItem>
          <Link href="https://zen-browser.app">Zen</Link> for everything. It is
          Firefox underneath, and a small push back against the Chromeopoly. It
          needs its own sync for Zen-specific settings.
        </ListItem>
        <ListItem>
          <Link href="https://njal.la">Njalla</Link>{' '}
          for domains, when I&apos;d rather not have my name on the WHOIS
        </ListItem>
        <ListItem>
          <Link href="https://cloudflare.com">Cloudflare</Link> in front of it
        </ListItem>
      </Section>

      <ProjectSection padding="none" className={styles.section}>
        <ProjectSectionContent>
          <div className={styles.tierListSection}>
            <h2 className={styles.tierListHeading}>Tier List</h2>
            <div className={styles.tierList}>
              {tiers.map(({ tier, items }) => (
                <div key={tier} className={styles.tierRow}>
                  <div className={`${styles.tierLabel} ${styles[`tier${tier}`]}`}>
                    {tier}
                  </div>
                  <div className={styles.tierItems}>
                    {items.map(({ name, url, note }) => (
                      <a
                        key={name}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.tierItem}
                        title={note}
                      >
                        {name}
                        {note && <span className={styles.tierItemNote}>{note}</span>}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ProjectSectionContent>
      </ProjectSection>
    </>
  );
}
